import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { LoadState } from "@redotech/react-util/load";
import { isVariantInStock } from "@redotech/redo-customer-portal-shared/flow/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { Button, ButtonSize, ButtonTheme } from "@redotech/redo-web/button";
import { CurrencyContext } from "@redotech/redo-web/currency";
import Cart from "@redotech/redo-web/icon-old/cart.svg";
import ModalCloseButton from "@redotech/redo-web/icon-old/modal-close-button.svg";
import htmlReactParser from "html-react-parser";
import { memo, useContext, useState } from "react";
import * as advancedExchange from "./advanced-exchange.module.css";
import {
  ExchangeProduct,
  ExchangeProductVariant,
  VariantBubbles,
} from "./variant-bubbles";

export const ItemDetailsModal = memo(function ItemDetailsModal({
  open,
  setOpen,
  onItemSelected,
  onItemSelectedLoad,
  selectedProduct,
  settings,
  exchangeGroup,
  returnType,
}: {
  open: boolean;
  setOpen: (x: boolean) => void;
  onItemSelected: (x: ExchangeProductVariant | null) => void;
  onItemSelectedLoad?: LoadState<void | undefined>;
  selectedProduct: ExchangeProduct | null;
  settings: ReturnAppSettings;
  exchangeGroup?: ExchangeGroup;
  returnType: ReturnTypeEnum | undefined;
}) {
  const handleClose = () => setOpen(false);
  const [showErrors, setShowErrors] = useState(false);
  const [selectedVariant, setSelectedVariant] =
    useState<ExchangeProductVariant | null>(null);
  const { formatCurrency } = useContext(CurrencyContext);

  const singleVariantOutOfStock = () => {
    return (
      selectedProduct != null &&
      selectedProduct.variants.length === 1 &&
      !isVariantInStock({
        settings,
        returnType,
        availableForSale: selectedProduct.variants[0].availableForSale,
        inventoryQuantity: selectedProduct.variants[0].quantityAvaliable,
      })
    );
  };

  const isButtonDisabled = (): boolean => {
    // Button is disabled when:
    //  a) an item has more than one variant and no variant is selected
    //  b) the item only has one variant (i.e. the mercant has actually created
    //     any variants for the item so it is the default variant) and that variant
    //     is out of stock
    return (
      (!selectedVariant &&
        selectedProduct != null &&
        selectedProduct.variants.length > 1) ||
      singleVariantOutOfStock()
    );
  };

  const imageSource = () => {
    if (!selectedProduct) {
      return "";
    }

    return selectedVariant && selectedVariant.image
      ? selectedVariant.image
      : selectedProduct.image;
  };

  return (
    <div>
      <Modal
        className={advancedExchange.itemDetailsModalContainer}
        onClose={handleClose}
        open={open}
      >
        <Box className={advancedExchange.itemDetailsModal}>
          <ModalCloseButton
            className={advancedExchange.itemDetailsModalExit}
            onClick={handleClose}
          />
          <Box className={advancedExchange.itemDetailsModalContent}>
            <Box className={advancedExchange.itemDetailsPrimaryContainer}>
              <img
                alt="product image"
                className={advancedExchange.itemDetailsModalImage}
                src={imageSource()}
              />
            </Box>
            <Box className={advancedExchange.itemDetailsSecondaryContainer}>
              <Box className={advancedExchange.itemDetailsInfoWrapper}>
                <Box className={advancedExchange.itemDetailsModalInfo}>
                  <Typography
                    className={advancedExchange.itemDetailsModalTitle}
                    variant="h6"
                  >
                    {selectedProduct ? selectedProduct.title : ""}
                  </Typography>
                  {exchangeGroup?.priceDifference !== "ignore" && (
                    <Typography
                      className={advancedExchange.itemDetailsModalPrice}
                    >
                      {selectedVariant
                        ? formatCurrency(selectedVariant.price)
                        : selectedProduct
                          ? formatCurrency(selectedProduct.price)
                          : ""}
                    </Typography>
                  )}
                  {singleVariantOutOfStock() && (
                    <Typography
                      className={`${advancedExchange.itemDetailsModalPrice} ${advancedExchange.textDanger}`}
                    >
                      Out of stock
                    </Typography>
                  )}
                  {selectedProduct && selectedProduct.variants.length > 1 && (
                    <VariantBubbles
                      productData={selectedProduct}
                      returnType={returnType}
                      setSelectedVariant={setSelectedVariant}
                      settings={settings}
                      showErrors={showErrors}
                    />
                  )}
                  {selectedProduct && selectedProduct.description != "" ? (
                    <>
                      <Typography
                        className={
                          advancedExchange.itemDetailsModalDescriptionHeader
                        }
                      >
                        Description
                      </Typography>
                      <Typography
                        className={advancedExchange.itemDetailsModalDescription}
                        variant="body2"
                      >
                        {selectedProduct
                          ? htmlReactParser(selectedProduct.description || "")
                          : ""}
                      </Typography>
                    </>
                  ) : null}
                </Box>
              </Box>
              <div onClick={() => setShowErrors(true)}>
                <Button
                  className={advancedExchange.itemDetailsButton}
                  disabled={isButtonDisabled()}
                  onClick={() => {
                    if (
                      selectedProduct?.variants?.length === 1 &&
                      !singleVariantOutOfStock()
                    ) {
                      onItemSelected(selectedProduct.variants[0]);
                    } else {
                      onItemSelected(selectedVariant);
                    }
                  }}
                  pending={onItemSelectedLoad?.pending}
                  size={ButtonSize.MEDIUM}
                  theme={ButtonTheme.PRIMARY}
                >
                  {exchangeGroup ? (
                    "Choose item"
                  ) : (
                    <>
                      <Cart
                        className={advancedExchange.itemDetailsButtonIcon}
                        height="20"
                        width="21"
                      />
                      Add to cart
                    </>
                  )}
                </Button>
              </div>
            </Box>
          </Box>
        </Box>
      </Modal>
    </div>
  );
});
